import React from "react";
import { flagUrl, flagUrlSquare } from "../../shared/flags";

// Dev gallery: variations of "flag = its own card" (layout #3) where the flag COMPLETELY
// covers its box (full width + height, edge to edge). Varies the box proportion and corner
// rounding. Route: /flagcard
const NAME = "VioletVortex";
const CODE = "pt";
const SWATCH = "#37c8e0";
const CARD_BG = "#141b2b";
const LINE = "rgba(255,255,255,0.10)";
const H = 60;

const swatch = <span style={{ width: 26, height: 26, borderRadius: 5, background: SWATCH, flex: "0 0 auto" }} />;
const name = <span style={{ fontFamily: "'Rajdhani',sans-serif", fontWeight: 800, fontSize: 18, color: "#eaf0ff", flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{NAME}</span>;
const keyBtn = (t: string) => <span style={{ fontFamily: "'Rajdhani',sans-serif", fontWeight: 800, fontSize: 12, color: "#cfd6e6", border: `1px solid ${LINE}`, borderRadius: 5, padding: "6px 10px", background: "rgba(255,255,255,0.03)" }}>{t}</span>;
const addChip = <span style={{ fontFamily: "'Rajdhani',sans-serif", fontWeight: 700, fontSize: 12.5, color: SWATCH, border: `1px dashed rgba(55,200,224,0.5)`, background: "rgba(55,200,224,0.06)", borderRadius: 8, padding: "7px 11px", whiteSpace: "nowrap" }}>+ Add player</span>;
const keys = <span style={{ display: "inline-flex", gap: 6, flex: "0 0 auto" }}>{keyBtn("LEFT")}{keyBtn("A")}</span>;

const cardBase: React.CSSProperties = { display: "flex", alignItems: "center", height: H, boxSizing: "border-box", background: CARD_BG, border: `1px solid ${LINE}`, borderRadius: 8, overflow: "hidden" };

// A flag card whose flag covers the entire box. `w` width, `radius` corner, `round` uses a
// circular box + the round flag; otherwise the square flag fills via object-fit cover.
function FlagCard({ w, radius, round, border }: { w: number; radius: number; round?: boolean; border?: boolean }) {
  return (
    <div style={{ width: round ? H : w, height: H, borderRadius: round ? "50%" : radius, overflow: "hidden", flex: "0 0 auto", cursor: "pointer", border: border ? `1px solid ${LINE}` : "none", boxSizing: "border-box" }}>
      <img src={round ? flagUrl(CODE) : flagUrlSquare(CODE)} alt="" style={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }} />
    </div>
  );
}

const row: React.CSSProperties = { display: "flex", gap: 10, alignItems: "stretch" };
const playerFull = <div style={{ ...cardBase, gap: 12, padding: "0 12px", flex: 1, minWidth: 0 }}>{swatch}{name}{keys}{addChip}</div>;
const playerNoAdd = <div style={{ ...cardBase, gap: 12, padding: "0 12px", flex: 1, minWidth: 0 }}>{swatch}{name}{keys}</div>;
const playerAddMid = <div style={{ ...cardBase, gap: 12, padding: "0 12px", flex: 1, minWidth: 0 }}>{swatch}{name}{addChip}{keys}</div>;
const addBox = <div style={{ ...cardBase, border: "1px dashed rgba(55,200,224,0.5)", background: "rgba(55,200,224,0.06)", color: SWATCH, cursor: "pointer", padding: "0 16px", justifyContent: "center", fontFamily: "'Rajdhani',sans-serif", fontWeight: 700, fontSize: 13, whiteSpace: "nowrap" }}>+ Add player</div>;
const flag43 = <FlagCard w={Math.round(H * 1.33)} radius={8} border />;

const VARIANTS: { n: number; label: string; node: React.ReactNode }[] = [
  { n: 1, label: "Square, sharp corners", node: <div style={row}><FlagCard w={H} radius={0} />{playerFull}</div> },
  { n: 2, label: "Square, rounded", node: <div style={row}><FlagCard w={H} radius={8} border />{playerFull}</div> },
  { n: 3, label: "Landscape 4:3, rounded", node: <div style={row}><FlagCard w={Math.round(H * 1.33)} radius={8} border />{playerFull}</div> },
  { n: 4, label: "Landscape 3:2 (true flag ratio)", node: <div style={row}><FlagCard w={Math.round(H * 1.5)} radius={8} border />{playerFull}</div> },
  { n: 5, label: "Wide 2:1", node: <div style={row}><FlagCard w={Math.round(H * 2)} radius={8} border />{playerFull}</div> },
  { n: 6, label: "Round card (round flag fills it)", node: <div style={row}><FlagCard w={H} radius={0} round />{playerFull}</div> },
  { n: 7, label: "#4: Add player in its own box, between player and flag", node: <div style={row}>{playerNoAdd}{addBox}{flag43}</div> },
  { n: 8, label: "#4: Add player between the name and keys (inside the card)", node: <div style={row}>{playerAddMid}{flag43}</div> },
];

export default function FlagCardDemo() {
  return (
    <div style={{ maxWidth: 820, margin: "0 auto", padding: "8px 16px 60px", fontFamily: "'Rajdhani', sans-serif", color: "#eaf0ff" }}>
      <h1 style={{ fontFamily: "'Orbitron', sans-serif", letterSpacing: 2, fontSize: 22 }}>Flag card fills its box</h1>
      <p style={{ color: "#8995b3", fontSize: 14, marginTop: 4 }}>
        Variations of &ldquo;flag = its own card&rdquo; where the flag <b>completely covers</b> its box (full width and
        height, edge to edge). Varies the box proportion and corner rounding.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 16, marginTop: 16 }}>
        {VARIANTS.map((v) => (
          <div key={v.n} style={{ background: "#0b0f17", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, padding: "14px 16px" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
              <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, color: "#37c8e0", fontSize: 15 }}>{v.n}</span>
              <span style={{ fontSize: 13, fontWeight: 600, color: "#c3ccdf" }}>{v.label}</span>
            </div>
            {v.node}
          </div>
        ))}
      </div>
    </div>
  );
}
